import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const RevenuePerformance = ({ data }) => {
  const { t } = useTranslation();
  const { series, options, stats } = useMemo(() => {
    const performanceData = data?.performance14Days || {};

    const dailyData = performanceData.dailyData || [];

    const categories = dailyData.map((d) =>
      new Date(d.date).toLocaleDateString("en", {
        month: "short",
        day: "2-digit",
      })
    );

    const revenueData = dailyData.map((d) => d.revenue || 0);

    return {
      stats: {
        totalBooked: performanceData.totalBooked || 0,
        totalRevenue: performanceData.totalRevenue || 0,
        adr: performanceData.averageDailyRate || 0,
      },
      series: [
        {
          name: t("finance.metrics.revenue") + " (฿)",
          data: revenueData,
        },
      ],
      options: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          curve: "smooth",
          width: 3,
          colors: ["#E8307A"],
        },
        fill: {
          type: "solid",
          opacity: 0.15,
          colors: ["#E8307A"],
        },
        markers: {
          size: 5,
          colors: ["#fff"],
          strokeColors: "#E8307A",
          strokeWidth: 2,
          hover: { size: 7 },
        },
        xaxis: {
          categories,
          labels: {
            style: { colors: "#64748B", fontSize: "11px" },
          },
          axisTicks: { show: false },
          axisBorder: { show: false },
        },
        yaxis: {
          min: 0,
          tickAmount: 5,
          labels: {
            formatter: (val) =>
              val >= 1000 ? `฿${(val / 1000).toFixed(1)}k` : `฿${val}`,
            style: { colors: "#64748B", fontSize: "11px" },
          },
          title: {
            text: t("finance.metrics.revenue") + " (฿)",
            style: {
              color: "#64748B",
              fontWeight: 600,
              fontSize: "12px",
            },
          },
        },
        grid: {
          strokeDashArray: 3,
          borderColor: "#EFE2EB",
          padding: { right: 20 },
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: (value) =>
              "฿" +
              value?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
          },
        },
        colors: ["#E8307A"],
      },
    };
  }, [data]);

  return (
    <Card
      className="shadow-sm p-4 rounded-4"
      style={{ border: "1px solid #f1f1f1" }}
    >
      <h6 className="fw-bold mb-3">{t("finance.metrics.revenueGeneration")}</h6>

      <div
        className="d-flex flex-wrap align-items-center gap-4 mb-3"
        style={{ fontSize: "14px" }}
      >
        <div>
          <span className="text-muted">{t("finance.metrics.totalBooked")}:</span>{" "}
          <strong className="text-dark">{stats.totalBooked}</strong>
        </div>

        <div>
          <span className="text-muted">{t("finance.metrics.totalRevenue")}:</span>{" "}
          <strong style={{ color: "#E8307A" }}>
            ฿
            {stats.totalRevenue.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </strong>
        </div>

        <div>
          <span className="text-muted">{t("finance.metrics.averageDailyRate")}:</span>{" "}
          <strong style={{ color: "#E8307A" }}>
            ฿
            {stats.adr.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </strong>
        </div>
      </div>

      {series[0].data.length > 0 ? (
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={280}
        />
      ) : (
        <div className="text-center text-muted py-5">
          {t("finance.metrics.noRevenueData")}
        </div>
      )}
    </Card>
  );
};

export default RevenuePerformance;
